import React from 'react';
import { Box, Typography } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import type { LocationMapItem, EntryExitItem } from './mapTypes';
import { entryCenterX, entryCenterY, sortOrderToColor } from './mapTypes';

interface MapSortOrderOverlayProps {
  locations: LocationMapItem[];
  entryExits: EntryExitItem[];
  cellPx: number;
  visible: boolean;
}

const MapSortOrderOverlay: React.FC<MapSortOrderOverlayProps> = ({
  locations, entryExits, cellPx, visible,
}) => {
  if (!visible) return null;

  const racks = locations.filter(l => l.type === 'Rack' && l.sortOrder > 0);
  if (racks.length === 0) return null;
  const maxSO = Math.max(...racks.map(r => r.sortOrder));

  const aisles = locations.filter(l => l.type === 'Aisle');
  const entry = entryExits.find(e => e.nodeType === 'Entry');
  const exit = entryExits.find(e => e.nodeType === 'Exit');

  // Determine aisle direction arrows based on rack positions
  const aisleDirections = aisles.map(aisle => {
    const relatedRacks = racks.filter(r => r.aisleId === aisle.id);
    if (relatedRacks.length < 2) return null;
    const sorted = [...relatedRacks].sort((a, b) => a.sortOrder - b.sortOrder);
    const first = sorted[0];
    const last = sorted[sorted.length - 1];
    const goingDown = last.gridY > first.gridY;
    return { aisle, goingDown };
  }).filter(Boolean) as { aisle: LocationMapItem; goingDown: boolean }[];

  // Entry → first rack dashed line
  const firstRack = racks.reduce((a, b) => a.sortOrder < b.sortOrder ? a : b);
  const lastRack = racks.reduce((a, b) => a.sortOrder > b.sortOrder ? a : b);

  return (
    <>
      {/* SortOrder badges on racks */}
      {racks.map(rack => {
        const color = sortOrderToColor(rack.sortOrder, maxSO);
        return (
          <Box key={`so-${rack.id}`} sx={{
            position: 'absolute',
            left: rack.gridX * cellPx + (rack.gridWidth * cellPx) / 2,
            top: rack.gridY * cellPx + (rack.gridHeight * cellPx) / 2,
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none',
            zIndex: 30,
          }}>
            <Box sx={{
              width: Math.max(16, cellPx * 0.7),
              height: Math.max(16, cellPx * 0.7),
              borderRadius: '50%',
              bgcolor: color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1.5px solid #fff',
              boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
            }}>
              <Typography sx={{
                fontSize: Math.max(7, cellPx * 0.3),
                fontWeight: 800,
                color: '#fff',
                lineHeight: 1,
                userSelect: 'none',
              }}>
                {rack.sortOrder}
              </Typography>
            </Box>
          </Box>
        );
      })}

      {/* Aisle direction arrows */}
      {aisleDirections.map(({ aisle, goingDown }) => (
        <Box key={`dir-${aisle.id}`} sx={{
          position: 'absolute',
          left: aisle.gridX * cellPx + (aisle.gridWidth * cellPx) / 2,
          top: aisle.gridY * cellPx + (aisle.gridHeight * cellPx) / 2,
          transform: 'translate(-50%, -50%)',
          pointerEvents: 'none',
          zIndex: 29,
        }}>
          {goingDown
            ? <ArrowDownwardIcon sx={{ fontSize: Math.max(14, cellPx * 0.6), color: '#1565c0', opacity: 0.7 }} />
            : <ArrowUpwardIcon sx={{ fontSize: Math.max(14, cellPx * 0.6), color: '#1565c0', opacity: 0.7 }} />
          }
        </Box>
      ))}

      {/* Dashed line: entry → first rack (peron üstünden geçmeyen L-şekilde) */}
      {entry && (() => {
        const segs = buildEntryRackSegments(
          { x: entryCenterX(entry) * cellPx, y: entryCenterY(entry) * cellPx },
          firstRack, cellPx,
        );
        return (
          <svg style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 28 }}>
            <polyline points={segs} stroke="#4caf50" strokeWidth={2} strokeDasharray="6,4" fill="none" />
          </svg>
        );
      })()}

      {/* Dashed line: last rack → exit */}
      {exit && (() => {
        const segs = buildEntryRackSegments(
          { x: entryCenterX(exit) * cellPx, y: entryCenterY(exit) * cellPx },
          lastRack, cellPx,
        );
        return (
          <svg style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 28 }}>
            <polyline points={segs} stroke="#ff9800" strokeWidth={2} strokeDasharray="6,4" fill="none" />
          </svg>
        );
      })()}
    </>
  );
};

/**
 * Rafın aisleSide'ına göre yürünebilir komşu hücrenin px-merkezi.
 * Peron koordinatına dokunmadan koridorun yürüme şeridine düşer.
 */
function rackWalkablePx(rack: LocationMapItem, cellPx: number): { x: number; y: number } {
  const cx = (rack.gridX + rack.gridWidth / 2) * cellPx;
  const cy = (rack.gridY + rack.gridHeight / 2) * cellPx;
  switch (rack.aisleSide) {
    case 'Left':   return { x: (rack.gridX + rack.gridWidth + 0.5) * cellPx, y: cy };
    case 'Right':  return { x: (rack.gridX - 0.5) * cellPx, y: cy };
    case 'Top':    return { x: cx, y: (rack.gridY + rack.gridHeight + 0.5) * cellPx };
    case 'Bottom': return { x: cx, y: (rack.gridY - 0.5) * cellPx };
    default:       return { x: cx, y: cy };
  }
}

/**
 * Entry/Exit ↔ Rack arası SortOrder gösterge çizgisi:
 *  Entry → corner (entry.X, walkable.Y) → walkable → rack.center
 *  Bu şekilde çizgi koridorun YÜRÜME şeridine girer, peron koordinatlarını kesmez.
 */
function buildEntryRackSegments(
  entryPx: { x: number; y: number },
  rack: LocationMapItem,
  cellPx: number,
): string {
  const walk = rackWalkablePx(rack, cellPx);
  const rackCx = (rack.gridX + rack.gridWidth / 2) * cellPx;
  const rackCy = (rack.gridY + rack.gridHeight / 2) * cellPx;
  // Köşe: önce dikey hareket → sonra yatay hareket (Left/Right rack için)
  // veya tersi (Top/Bottom rack için).
  const verticalFirst = rack.aisleSide === 'Left' || rack.aisleSide === 'Right' || !rack.aisleSide;
  const corner = verticalFirst
    ? { x: entryPx.x, y: walk.y }
    : { x: walk.x, y: entryPx.y };
  return [entryPx, corner, walk, { x: rackCx, y: rackCy }]
    .map(p => `${p.x},${p.y}`).join(' ');
}

export default MapSortOrderOverlay;
